import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { confirmDialog } from 'primereact/confirmdialog';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from './apiErrorHandler';

export const rs = (n) => `Rs. ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthOptions = () => {
  const out = [], d = new Date();
  d.setDate(1);
  for (let i = 0; i < 12; i++) {
    const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ label: v, value: v });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

const Stat = ({ label, value, sub }) => (
  <div className="surface-ground border-round p-3" style={{ minWidth: 180 }}>
    <div className="text-color-secondary text-sm">{label}</div>
    <div className="text-2xl font-bold">{value}</div>
    {sub && <div className="text-color-secondary text-xs mt-1">{sub}</div>}
  </div>
);

/** Organisation admin view of AI-read GRNs: live usage and cost in rupees. Shown only when Coffer Smart has set the account up. */
const AIGrnUsage = () => {
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async (m) => {
    try {
      const { data: d } = await axiosInstance.get('/api/ai-grn/usage/', { params: m ? { month: m } : {} });
      setData(d);
      if (!m) setMonth(d.selected_month);
    } catch (e) { handleApiError(e); }
  }, []);

  useEffect(() => { load(''); }, [load]);

  if (!data || !data.configured) return null;     // nothing to show for organisations without AI GRN

  const prepaid = data.billing_mode === 'prepaid';
  const switchOff = () => confirmDialog({
    header: 'Switch off AI reading?',
    message: 'Scan Bill will use the standard reader. To switch AI reading on again you will need to contact Coffer Smart.',
    icon: 'pi pi-exclamation-triangle', acceptLabel: 'Switch off', rejectLabel: 'Keep on',
    accept: async () => {
      try {
        const { data: d } = await axiosInstance.post('/api/ai-grn/disable/');
        setData((old) => ({ ...old, ...d }));
        handleApiSuccess('AI reading switched off');
      } catch (e) { handleApiError(e); }
    },
  });

  return (
    <section className="surface-card p-4 border-round shadow-1 mb-4" id="ai-grn-usage">
      <div className="flex align-items-center gap-2 mb-1">
        <h5 className="m-0">AI-powered GRN</h5>
        <Tag severity={data.enabled ? 'success' : 'danger'} value={data.enabled ? 'On' : 'Off'} />
        <Tag severity="info" value={prepaid ? 'Prepaid' : 'Postpaid (added to monthly bill)'} />
        {data.enabled && <Button label="Switch off" size="small" severity="danger" outlined className="ml-auto" onClick={switchOff} />}
      </div>
      <small className="text-color-secondary">
        Scan Bill reads supplier invoices with AI. Every scan is charged separately on a token basis.
        {!data.enabled && ' It is off; contact Coffer Smart to switch it on.'}
      </small>
      {data.enabled && !data.usable && <div className="mt-2"><Tag severity="warning" value={data.unusable_reason} /></div>}

      <div className="flex flex-wrap gap-3 mt-3">
        {prepaid
          ? <Stat label="Wallet balance" value={rs(data.balance_lkr)} sub="Top up by contacting Coffer Smart" />
          : <Stat label="This month so far" value={rs(data.month.cost_lkr)} sub={data.monthly_cap_lkr != null ? `Spending limit ${rs(data.monthly_cap_lkr)}` : 'Added to your monthly bill'} />}
        <Stat label={`Cost in ${data.selected_month}`} value={rs(data.selected.cost_lkr)} sub={`${data.selected.pages} bill pages read`} />
        <Stat label="Average per page" value={rs(data.selected.pages ? data.selected.cost_lkr / data.selected.pages : 0)} />
        <Stat label="All time" value={rs(data.lifetime_cost_lkr)} />
      </div>

      <div className="flex align-items-center gap-2 mt-4 mb-2">
        <b>Usage</b>
        <Dropdown value={month} options={monthOptions()} onChange={(e) => { setMonth(e.value); load(e.value); }} />
        <Button icon="pi pi-refresh" text rounded aria-label="Refresh" onClick={() => load(month)} />
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead><tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}><th>When</th><th>Photo</th><th>By</th><th>Cost</th></tr></thead>
        <tbody>
          {data.recent.length === 0 && <tr><td colSpan={4} className="text-color-secondary py-2">No AI scans in this month.</td></tr>}
          {data.recent.map((r) => (
            <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td className="py-1">{new Date(r.at).toLocaleString()}</td>
              <td>{r.file || '—'}</td>
              <td>{r.by || '—'}</td>
              <td>{rs(r.cost_lkr)}{r.status !== 'ok' && <Tag className="ml-2" severity="warning" value="failed" />}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {prepaid && data.topups.length > 0 && (
        <>
          <b className="block mt-4 mb-2">Top-ups</b>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <tbody>
              {data.topups.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td className="py-1">{new Date(t.at).toLocaleDateString()}</td><td>{rs(t.amount_lkr)}</td><td>{t.reference || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};

export default AIGrnUsage;
